/**
 * AIAssistantTrigger.tsx
 *
 * The phone-tier way into the AI assistant: a button in the shell's mobile header,
 * rendered in the layout flow rather than floating over the page.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────────
 * The original trigger is a `fixed right-0 top-1/2 z-30` tab (AISidebar.tsx). Being
 * fixed and vertically centred, it sits over whatever content happens to be at the
 * middle of the right edge — and because it is a real button at `z-30`, above
 * `<main>`'s `z-10`, it wins the hit test. Eight tickets reported the consequence:
 * "tapping + Add Package opens the AI Assistant", "it covers the Add Document button
 * so it cannot be tapped", "it covers the Email option in both Comm Log rows". Every
 * detail-tab header puts its primary action at the right end of a `justify-between`
 * row, so as the page scrolls each one passes underneath in turn.
 *
 * Nothing reserved space for it, and reserving a 44px gutter app-wide would cost every
 * mobile page its width. The shell already renders a full-width 48px header below `md`
 * (`Layout.tsx`) carrying one left-aligned `SidebarTrigger` — the right side is empty.
 * Putting the trigger there costs no layout width, adds no chrome, and cannot overlap
 * anything, because it is in the flow.
 *
 * ── Why the floating tab survives at 768–1023 ───────────────────────────────────
 * That header is `md:hidden`. Widening it to `lg` would take 48px of content height
 * from every routed page in the tablet band, which is the trade AISidebar's docblock
 * rejected when the tab was introduced. So the tab keeps the band it was written for
 * and gives up the phone tier, where all of the reports come from.
 *
 * State lives in `useAISidebarStore`, so this shares one `toggle` with the tab and the
 * desktop rail with no prop drilling and no coupling to `AISidebar`.
 *
 * The permission gate is `Layout.tsx`'s (`hasPermission(26)`), the same one that mounts
 * `<AISidebar />` — this component does not re-check it.
 */
import { Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAISidebarStore } from '@/store/aiSidebarStore';

interface AIAssistantTriggerProps {
  className?: string;
}

export function AIAssistantTrigger({ className }: AIAssistantTriggerProps) {
  // Every hook unconditional and on its own line. `useAnyModalOpen() && useIsBelow(...)`
  // is the shape that short-circuited a useSyncExternalStore hook, threw React #310 and
  // blanked every page — see the warning carried in the three floating widgets.
  const isOpen = useAISidebarStore((s) => s.isOpen);
  const toggle = useAISidebarStore((s) => s.toggle);
  const pathname = useLocation().pathname;

  // Same two suppressions the floating tab applies: nothing to open when the overlay is
  // already up, and the assistant's own page is not a place to offer a shortcut to it.
  const onAssistantPage = pathname.startsWith('/ai-assistant');
  if (isOpen || onAssistantPage) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Open AI Assistant"
      title="AI Assistant"
      className={cn(
        // touch-target-44 is gated on `(max-width: 1023.98px) and (pointer: coarse)`,
        // so it sizes the tap area on touch without affecting a mouse user.
        'inline-flex items-center justify-center rounded-md text-indigo-600',
        // No `touch-target-inset` (SCRUM-1480). Its `margin: -0.75rem` shorthand set
        // `margin-left` too and beat the `ml-auto` this button used to carry, collapsing
        // it leftwards against the menu toggle and overhanging its hit box 12px further.
        // The header is `h-12` (48px), so the 44px box fits without pulling it back out
        // of the layout; the row is `justify-between` now, so there is no margin to lose.
        'touch-target-44 tap-feedback',
        'hover:bg-indigo-50 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {/* touch-target-icon sizes the glyph, not the box — on the button it declares
          1.25rem and would beat touch-target-44's 2.75rem. */}
      <Sparkles className="h-5 w-5 touch-target-icon" />
    </button>
  );
}
